import { Link } from "@remix-run/react";
import { useTranslation } from "react-i18next";

import qrCertificado from "~/assets/qr-certificado.png";

import { AcademiaSeal } from "../CuadernoVivo/AcademiaSeal";
import { TornEdge } from "../CuadernoVivo/TornEdge";

/**
 * La credencial verificable. Replanteo 2026-07-31: antes eran cuatro features con
 * título Y descripción (≈120 palabras) al lado del ejemplo — el texto le ganaba al
 * objeto, y nadie lee cuatro párrafos para entender una sola idea.
 *
 * Ahora la idea es una ("se comprueba contra nuestros registros"), las pruebas son tres
 * chips de una línea, y el protagonista es el objeto mismo: es lo único de esta sección
 * que no se puede contar con palabras. En mobile va primero.
 *
 * Es copy GENERAL de la home, no de un curso: por eso nombra a las dos credenciales
 * (constancia en los gratis, certificado en los pagos) en vez de prometer certificado a
 * secas. La promesa por curso se decide con `credentialType` en la ficha.
 */
export function CertificateSection() {
  const { t } = useTranslation();
  const proofs = t("landing.launch.cert.proofs", { returnObjects: true }) as string[];

  return (
    // La banda OSCURA de la home. Es la misma que traía "Rutas de aprendizaje", que dejó
    // este lugar: se migró el tratamiento, no sólo el contenido.
    //
    // Por qué el degradado tiene 1px del color vecino arriba y abajo: la caja cae en
    // coordenada fraccionaria y con devicePixelRatio no entero (Windows al 125%, zoom del
    // navegador) esa fila se mezcla con lo de al lado y dibuja una línea horizontal de
    // lado a lado. Arrancando el oscuro 1px adentro, esa mezcla es vecino con vecino:
    // invisible en todos los DPR.
    //
    // Los DOS vecinos usan `bg-landing-white`, así que el borde LEE EL TOKEN en vez de un
    // hex fijo — en modo oscuro ese token vale 46 38 32 y el papel rasgado lo sigue solo.
    <section className="landing-grain relative overflow-hidden px-5 pb-[104px] pt-[104px] sm:px-8 sm:pt-[120px]">
      <div
        aria-hidden="true"
        className="absolute inset-0"
        style={{
          // Los tonos de la banda son variables porque en modo oscuro tienen que bajar:
          // con los valores de claro, el borde inferior quedaba casi del mismo color que
          // la sección vecina y el corte con el papel rasgado desaparecía.
          background:
            "linear-gradient(180deg, rgb(var(--landing-white)) 0 1px, rgb(var(--landing-band-top)) 1px, rgb(var(--landing-band-top)) 62%, rgb(var(--landing-band-bottom)) calc(100% - 1px), rgb(var(--landing-white)) calc(100% - 1px))",
        }}
      />
      <TornEdge color="rgb(var(--landing-white))" className="top-0" />
      <TornEdge color="rgb(var(--landing-white))" className="bottom-0" flip />
      <div className="relative z-10 mx-auto grid max-w-[1100px] items-center gap-10 md:grid-cols-[0.9fr_1.1fr] md:gap-14">
        <div>
          <h2 className="font-display text-balance text-[clamp(24px,3.2vw,34px)] font-extrabold leading-tight tracking-tight text-landing-on-dark">
            {t("landing.launch.cert.title")}
          </h2>
          <p className="mt-3 max-w-[46ch] text-[15.5px] leading-relaxed text-[rgba(255,255,255,0.72)]">
            {t("landing.launch.cert.lead")}
          </p>
          {/* Uno por renglón, en desktop y en mobile. Con el separador "·" en una sola
              línea, frases como "No se puede falsificar" se partían al medio; y son
              tres afirmaciones distintas, no una enumeración corrida. */}
          <ul className="mt-5 flex flex-col gap-2">
            {proofs.map((proof) => (
              <li
                key={proof}
                className="landing-meta uppercase leading-relaxed tracking-[0.13em] text-[rgba(255,255,255,0.65)]"
              >
                {proof}
              </li>
            ))}
          </ul>
          <Link
            to="/certificado"
            data-cuelume-hover="tick"
            data-cuelume-press
            className="mt-6 inline-flex items-center gap-1.5 text-[14.5px] font-bold text-[#7ecfd8] underline decoration-[#7ecfd8]/40 underline-offset-4 transition-colors hover:decoration-[#7ecfd8]"
          >
            {t("landing.launch.cert.seeExample")} →
          </Link>
        </div>

        {/* El objeto. En mobile va PRIMERO: es la prueba, no la ilustración.

            `landing-media-paper` (papel claro fijo) en vez de `bg-landing-paper`: el
            token se invierte en modo oscuro y el certificado quedaba en #2A231C sobre
            esta banda #1c1713, o sea casi invisible. Un certificado es papel en los dos
            modos, igual que las ilustraciones. Por eso el texto de adentro también lleva
            colores fijos y no tokens. */}
        <div className="landing-media-paper relative mx-auto w-full max-w-[520px] rotate-[0.6deg] rounded-[6px] border-2 border-[#e5dfd3] p-8 shadow-landing-xl max-md:order-first sm:p-9">
          <span
            aria-hidden="true"
            className="pointer-events-none absolute inset-[9px] rounded-[3px] border border-[#C0392B]/35"
          />
          <AcademiaSeal size={64} className="absolute -top-4 right-6 rotate-[8deg]" />
          <p className="font-display text-[16px] font-bold text-[#1F1A15]">
            Academ<span className="text-[#C0392B]">IA</span>
          </p>
          {/* El nombre, el curso y el token de abajo son inventados. Sin esta marca, una
              captura de esta tarjeta circula suelta como si fuera una credencial emitida
              —el mismo motivo por el que /certificado lleva marca de agua—. */}
          <p className="mt-1 text-[10px] font-bold uppercase tracking-[0.2em] text-[#70685E]">
            Ejemplo · datos de muestra
          </p>
          <p className="mt-4 text-[10px] uppercase tracking-[0.2em] text-[#70685E]">
            {t("landing.launch.cert.example.certifies")}
          </p>
          <p className="landing-hand mt-1 text-[27px] leading-none text-[#1F1A15]">
            {t("landing.launch.cert.example.name")}
          </p>
          <p className="mt-3 text-[10px] uppercase tracking-[0.2em] text-[#70685E]">
            {t("landing.launch.cert.example.completed")}
          </p>
          <p className="font-display mt-1 text-[17px] text-[#1F1A15]">
            {t("landing.launch.cert.example.course")}
          </p>
          <div className="mt-6 flex items-end justify-between gap-4">
            <p className="text-[10.5px] leading-relaxed text-[#70685E] [font-variant-numeric:tabular-nums]">
              {t("landing.launch.cert.example.issued")}: 07 / 2026
              <br />
              {t("landing.launch.cert.example.token")}:{" "}
              <b className="text-[#2B7D87]">#A1B2·C3D4·E5F6</b>
              <br />
              {t("landing.launch.cert.example.verifyAt")}
            </p>
            <Link
              to="/certificado"
              aria-label="Ver ejemplo de certificado verificado"
              data-cuelume-hover="tick"
              data-cuelume-press
              className="block shrink-0 transition-transform hover:scale-105"
            >
              <img
                src={qrCertificado}
                alt="Código QR de verificación"
                width={62}
                height={62}
                className="h-[62px] w-[62px] rounded-[6px] border-[3px] border-white shadow-[0_0_0_1.5px_#E5DFD3]"
              />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}
